import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, ConvertPurchaseOrderVerify, ConvertPurchaseOrderToPurchase } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { useLocation } from 'react-router-dom';
import { Search, Add, ShoppingCart, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, ExpandMore, ExpandLess, CheckCircleOutline, Print, Visibility, Edit, Delete } from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Collapse, Divider,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery
} from '@mui/material';
import PurchaseOrderModal from './PurchaseOrderModal';
import Pagination from '../../common/Pagination';
import MampillyLogo from '../Logo/logo_Madavana.png';

const PurchaseOrderIndex = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, userId, permissions, companyName } = useAuth();
  const { logAudit } = useAuditLog();
  const location = useLocation();
  const reorderHandledRef = useRef(false);

  //-----------------PERMISSION--------------------
  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);

  // Inactive modal states
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [inactiveViewData, setInactiveViewData] = useState(null);
  const [inactiveViewDetails, setInactiveViewDetails] = useState([]);

  // Supplier drill-down modal states
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPurchaseOrders, setSupplierPurchaseOrders] = useState([]);
  const [expandedPOId, setExpandedPOId] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Debounced search to prevent excessive filtering
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Default fromDate to first day of current month, toDate to today
  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getDefaultToDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getDefaultToDate());
  const isInitialMount = useRef(true);

  // Re-fetch when dates change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    if (companyId && token) {
      fetchPurchaseOrders();
      setCurrentPage(1);
    }
  }, [fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [orderDetails, setOrderDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  // Activate confirmation modal
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  useEffect(() => {
    if (companyId && token) {
      fetchAllData();
    }
  }, [companyId, token]);

  // Auto-open create modal with reorder items from dashboard navigation
  useEffect(() => {
    const reorderItems = location.state?.reorderItems;
    if (!reorderItems?.length || reorderHandledRef.current) return;
    if (!companyId || !token) return;

    reorderHandledRef.current = true;

    const openReorderModal = async () => {
      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();
      const freightGSTRate = gstData.freight?.GSTRate || gstData.freight?.GstRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || gstData.packing?.GstRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        SupplierId: '',
        InvoiceTypeId: 0,
        PONumber: maxBillData.billNumber,
        PurchaseOrderNo: maxBillData.purchaseOrderNo,
        PurchaseOrderDate: new Date().toISOString().split('T')[0],
        FinYear: maxBillData.finYear,
        Instruction1: '',
        Instruction2: '',
        Remarks: '',
        Amount: 0, DiscountAmount: 0, TaxableAmount: 0,
        CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0,
        FreightAmount: 0, FreightGSTRate: freightGSTRate, FreightGSTAmount: 0,
        PackingAmount: 0, PackingGSTRate: packingGSTRate, PackingGSTAmount: 0,
        RoundOff: 0, TotalAmount: 0,
        IsConverted: false, IsVerified: false, IsActive: true,
      });
      setOrderDetails(reorderItems.map(item => {
        const rate = parseFloat(item.Rate) || 0;
        const gstRate = parseFloat(item.GSTRate) || 0;
        const taxableAmount = rate; // qty=1, no discount
        const gstAmount = (taxableAmount * gstRate) / 100;
        return {
          Id: 0,
          ItemId: item.ItemId,
          ItemName: item.ItemName || '',
          HSNCode: item.HSNCode || '',
          GSTRate: gstRate,
          Quantity: 1,
          Rate: rate,
          Amount: rate,
          DiscountPercentage: 0,
          DiscountAmount: 0,
          TaxableAmount: taxableAmount,
          CGSTAmount: gstAmount / 2,
          SGSTAmount: gstAmount / 2,
          IGSTAmount: 0,
          TotalAmount: taxableAmount + gstAmount,
          IsActive: true,
        };
      }));
      setModalAction('create');
      setOpenModal(true);
    };

    openReorderModal();
  }, [companyId, token, location.state]);


  // Fetch functions
  const fetchPurchaseOrders = async () => {
    try {
      // Fetch active purchase orders
      const activeResponse = await fetchData('PurchaseOrderApi', `/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: true }));

      // Fetch inactive purchase orders
      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PurchaseOrderApi', `/InActivePurchaseOrderListDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive purchase orders:', inactiveError);
      }

      // Combine both active and inactive
      setPurchaseOrders([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      setPurchaseOrders([]);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      setSuppliers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  /**
   * Fetch items with optional search functionality - Optimized with debouncing
   * @param {string} inputValue - Optional search term to filter items by name
   */
  const fetchItems = useCallback(async (inputValue = '') => {
    try {
      // Construct API path with optional search parameter
      // If inputValue is provided, use the search API: /Item/{companyId}/{branchId}/{searchTerm}
      // Otherwise, use the default API: /Item/{companyId}
      const apiPath = inputValue
        ? `/${companyId}/${branchId}/${encodeURIComponent(inputValue)}`
        : `/${companyId}`;

      const response = await fetchData('ItemApi', apiPath, token);
      const newItems = response.response_data || response || [];

      // Only update if items actually changed to prevent unnecessary re-renders
      setItems(prevItems => {
        if (JSON.stringify(prevItems) !== JSON.stringify(newItems)) {
          return newItems;
        }
        return prevItems;
      });
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  }, [companyId, branchId, token]);

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      console.log('MaxBillNo API Response:', response);
      const data = response.response_data || response || {};
      console.log('MaxBillNo Data:', data);

      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';

      // Generate PurchaseOrderNo based on available data
      let purchaseOrderNo = '';
      if (billPrefix && finYear) {
        purchaseOrderNo = `${billPrefix}/${billNumber}/${finYear}`;
      } else if (finYear) {
        purchaseOrderNo = `${billNumber}/${finYear}`;
      } else if (billPrefix) {
        purchaseOrderNo = `${billPrefix}/${billNumber}`;
      } else {
        purchaseOrderNo = `${billNumber}`;
      }

      return { billNumber, billPrefix, finYear, purchaseOrderNo };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '', purchaseOrderNo: '1' };
    }
  };

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Freight + Packing GST
  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      console.log('AccountHeadFreightAndPacking API Response:', response);
      const data = response.response_data || response || [];
      console.log('AccountHeadFreightAndPacking Data:', data);

      // Find Freight and Packing entries from the response
      let freightData = null;
      let packingData = null;

      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight') || item.AccountHeadName?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing') || item.AccountHeadName?.toLowerCase().includes('packing'));
      } else if (data) {
        // If it's an object with Freight and Packing properties
        freightData = data.Freight || data.FreightCharge || null;
        packingData = data.Packing || data.PackingCharge || null;
      }

      setFreightPackingGST({
        freight: freightData,
        packing: packingData
      });

      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };


  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setLoadingMessage('Fetching purchase orders...');
    await Promise.allSettled([fetchPurchaseOrders(), fetchSuppliers(), fetchItems(), fetchFreightAndPackingGST()]);
    setLoading(false);
  }, [fetchItems]);

  const fetchSinglePurchaseOrder = async (id) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/${id}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching purchase order details:', error);
      return null;
    }
  };

  // Fetch inactive PO details for view modal
  const fetchInactivePODetails = async (id) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/InActivePurchaseOrderListDetails/${id}/${branchId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching inactive purchase order details:', error);
      return null;
    }
  };

  // Activate confirmation handlers
  const handleOpenActivateModal = (id, poNo) => {
    setActivateItemId(id);
    setActivateItemNo(poNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  // Activate inactive purchase order
  const handleActivate = async () => {
    if (!activateItemId) return;

    setLoading(true);
    handleCloseActivateModal();
    setLoadingMessage('Activating purchase order...');
    try {
      await activateData('PurchaseOrderApi', `${activateItemId}`, token);
      setSuccess('Purchase order activated successfully!');
      await fetchAllData();
    } catch (error) {
      console.error('Error activating record:', error);
      setError('Failed to activate purchase order.');
    }
    setLoading(false);
  };

  // Open inactive view modal
  const handleOpenInactiveModal = async (item) => {
    setLoading(true);
    setLoadingMessage('Fetching inactive purchase order details...');

    const freshData = await fetchInactivePODetails(item.Id);

    if (freshData) {
      const poData = freshData.PurchaseOrder || freshData;
      const details = freshData.PurchaseOrderDetails || freshData.Details || [];
      setInactiveViewData(poData);
      setInactiveViewDetails(details);
    } else {
      setInactiveViewData(item);
      setInactiveViewDetails([]);
    }

    setLoading(false);
    setInactiveModalOpen(true);
  };

  // Close inactive view modal
  const handleCloseInactiveModal = () => {
    setInactiveModalOpen(false);
    setInactiveViewData(null);
    setInactiveViewDetails([]);
  };

  // Fetch PO history for modal (when supplier is selected in create mode)
  const fetchPOHistoryForModal = async (supplierId) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/PurchaseOrderHistoryByMaster/${supplierId}/${branchId}`, token);
      const responseData = response.response_data || response || [];

      let purchaseOrdersList = [];
      let purchaseOrderDetailsList = [];

      if (Array.isArray(responseData) && responseData.length > 0) {
        const firstItem = responseData[0];
        purchaseOrdersList = firstItem.PurchaseOrder || firstItem.purchaseOrder || [];
        purchaseOrderDetailsList = firstItem.PurchaseOrderDetails || firstItem.purchaseOrderDetails || [];
      } else if (responseData.PurchaseOrder) {
        purchaseOrdersList = responseData.PurchaseOrder || [];
        purchaseOrderDetailsList = responseData.PurchaseOrderDetails || [];
      }

      // Map details to each purchase order
      const purchaseOrdersWithDetails = purchaseOrdersList.map(po => {
        const details = purchaseOrderDetailsList.filter(detail => detail.PurchaseOrderId === po.Id);
        return { ...po, PurchaseOrderDetails: details };
      });

      return purchaseOrdersWithDetails;
    } catch (error) {
      console.error('Error fetching PO history for modal:', error);
      return [];
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    if (action === 'view') {
      const recordId = typeof itemOrId === 'object' ? (itemOrId?.Id || 0) : (itemOrId || 0);
      logAudit(AUDIT_ACTION.VIEW, 'PurchaseOrderApi', recordId);
    }
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setLoading(true);
      setLoadingMessage('Fetching purchase order details...');

      const freshData = await fetchSinglePurchaseOrder(itemId);

      if (freshData) {
        const poData = freshData.PurchaseOrder || freshData;
        const details = freshData.PurchaseOrderDetails || freshData.Details || [];

        // Get supplier name from MasterName or find from suppliers list
        const supplierName = poData.MasterName || suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId || (s.MasterId || s.Id) === poData.SupplierId)?.Name || '';

        // Get InvoiceType name from suppliers list
        const supplier = suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId || (s.MasterId || s.Id) === poData.SupplierId);
        const invoiceTypeName = poData.InvoiceTypeName || supplier?.InvoiceTypeName || supplier?.InvoiceType || '';

        setSelectedItem(poData);
        setFormData({
          Id: poData.Id,
          CompanyId: poData.CompanyId || companyId,
          BranchId: poData.BranchId || 0,
          SupplierId: poData.MasterId || poData.SupplierId,
          MasterName: supplierName,
          InvoiceTypeId: poData.InvoiceTypeId || 0,
          InvoiceTypeName: invoiceTypeName,
          PurchaseOrderNo: poData.PurchaseOrderNo,
          PurchaseOrderDate: poData.PurchaseOrderDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          FinYear: poData.FinYear || '',
          Instruction1: poData.Instruction1 || '',
          Instruction2: poData.Instruction2 || '',
          Remarks: poData.Remarks || '',
          Amount: poData.Amount || poData.amount || 0,
          DiscountAmount: poData.DiscountAmount || poData.discountAmount || 0,
          TaxableAmount: poData.TaxableAmount || poData.taxableAmount || 0,
          CGSTAmount: poData.CGSTAmount || poData.cgstAmount || poData.CgstAmount || 0,
          SGSTAmount: poData.SGSTAmount || poData.sgstAmount || poData.SgstAmount || 0,
          IGSTAmount: poData.IGSTAmount || poData.igstAmount || poData.IgstAmount || 0,
          FreightAmount: poData.FreightAmount || poData.freightAmount || 0,
          FreightGSTRate: poData.FreightGSTRate || poData.freightGSTRate || 0,
          FreightGSTAmount: poData.FreightGSTAmount || poData.freightGSTAmount || 0,
          PackingAmount: poData.PackingAmount || poData.packingAmount || 0,
          PackingGSTRate: poData.PackingGSTRate || poData.packingGSTRate || 0,
          PackingGSTAmount: poData.PackingGSTAmount || poData.packingGSTAmount || 0,
          RoundOff: poData.RoundOff || poData.roundOff || 0,
          TotalAmount: poData.TotalAmount || poData.totalAmount || 0,
          IsConverted: poData.IsConverted || false,
          IsVerified: poData.IsVerified || false,
          IsActive: poData.IsActive ?? true,
          // Audit fields
          CreatedBy: poData.CreatedBy || null,
          CreatedByName: poData.CreatedByName || '',
          CreatedDate: poData.CreatedDate || poData.CreatedOn || poData.CreatedAt || '',
          ModifiedByName: poData.ModifiedByName || poData.ModifiedBy || poData.UpdatedBy || '',
          ModifiedDate: poData.ModifiedDate || poData.ModifiedOn || poData.UpdatedOn || poData.UpdatedAt || ''
        });

        // Log the API response to debug
        console.log('PO Data from API:', poData);
        console.log('Amount fields:', {
          Amount: poData.Amount,
          TaxableAmount: poData.TaxableAmount,
          CGSTAmount: poData.CGSTAmount,
          SGSTAmount: poData.SGSTAmount,
          TotalAmount: poData.TotalAmount
        });
        setOrderDetails(details.map(d => {
          // Get item name from ItemName or find from items list
          const itemName = d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '';
          return {
            Id: d.Id || 0,
            ItemId: d.ItemId,
            ItemName: itemName,
            HSNCode: d.HSNCode || '',
            UOM: d.UOM || items.find(i => i.Id === d.ItemId)?.UOM || '',
            GSTRate: d.GSTRate || 0,
            Quantity: d.Quantity || 0,
            Rate: d.Rate || 0,
            Amount: d.Amount || 0,
            DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0,
            TaxableAmount: d.TaxableAmount || 0,
            CGSTAmount: d.CGSTAmount || 0,
            SGSTAmount: d.SGSTAmount || 0,
            IGSTAmount: d.IGSTAmount || 0,
            TotalAmount: d.TotalAmount || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy || null,
            CreatedDate: d.CreatedDate || d.CreatedOn || null
          };
        }));
      } else {
        const item = typeof itemOrId === 'object' ? itemOrId : null;
        setSelectedItem(item);
        setFormData(item || {});
        setOrderDetails([]);
      }

      setLoading(false);
    } else {
      // Create mode - fetch max bill number and freight/packing GST
      setLoading(true);
      setLoadingMessage('Generating PO number...');

      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();

      // Get GST rates from API data
      const freightGSTRate = gstData.freight?.GSTRate || gstData.freight?.GstRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || gstData.packing?.GstRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        SupplierId: '',
        InvoiceTypeId: 0,
        PONumber: maxBillData.billNumber,
        PurchaseOrderNo: maxBillData.purchaseOrderNo,
        PurchaseOrderDate: new Date().toISOString().split('T')[0],
        FinYear: maxBillData.finYear,
        Instruction1: '',
        Instruction2: '',
        Remarks: '',
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: freightGSTRate,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: packingGSTRate,
        PackingGSTAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        IsConverted: false,
        IsVerified: false,
        IsActive: true
      });
      setOrderDetails([]);
      setLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setOrderDetails([]);
  };


  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} purchase order...`);

      // Get selected supplier details for MasterId, MasterName and InvoiceTypeId
      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.SupplierId));
      const masterId = selectedSupplier?.MasterId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      // Try multiple field names for InvoiceTypeId
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || selectedSupplier?.invoiceTypeId ||
        selectedSupplier?.InvoiceType || selectedSupplier?.invoiceType ||
        formData.InvoiceTypeId || 0;

      // Debug log to see supplier data
      console.log('Selected Supplier:', selectedSupplier);
      console.log('InvoiceTypeId:', invoiceTypeId);

      // Calculate total quantity from all order details
      const totalQuantity = orderDetails.reduce((sum, detail) => sum + (parseFloat(detail.Quantity) || 0), 0);

      const dataToSend = {
        PurchaseOrder: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || 0,
          MasterId: masterId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          PurchaseOrderNo: formData.PurchaseOrderNo || '',
          PurchaseOrderDate: formData.PurchaseOrderDate || new Date().toISOString().split('T')[0],
          FinYear: formData.FinYear || '',
          Instruction1: formData.Instruction1 || '',
          Instruction2: formData.Instruction2 || '',
          Remarks: formData.Remarks || '',
          TotalQuantity: totalQuantity,
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(formData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          RoundOff: parseFloat((parseFloat(formData.RoundOff) || 0).toFixed(2)),
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          IsConverted: formData.IsConverted || false,
          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'edit' ? formData.CreatedDate : null,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        PurchaseOrderDetails: orderDetails.map(detail => {
          // Get item name from items dropdown
          const selectedItem = items.find(i => i.Id === parseInt(detail.ItemId));
          const itemName = selectedItem ? (selectedItem.Code ? `${selectedItem.Code} - ${selectedItem.Name}` : selectedItem.Name) : '';

          return {
            Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
            CompanyId: companyId,
            BranchId: formData.BranchId || 0,
            PurchaseOrderId: modalAction === 'edit' ? formData.Id : 0,
            ItemId: parseInt(detail.ItemId) || 0,
            ItemName: itemName,
            HSNCode: detail.HSNCode || '',
            UOM: detail.UOM || selectedItem?.UOM || selectedItem?.UnitOfMeasure || '',
            GSTRate: parseFloat(detail.GSTRate) || 0,
            Quantity: parseFloat(detail.Quantity) || 0,
            Rate: parseFloat(detail.Rate) || 0,
            Amount: parseFloat(detail.Amount) || 0,
            DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
            TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
            CGSTAmount: parseFloat(detail.CGSTAmount) || 0,
            SGSTAmount: parseFloat(detail.SGSTAmount) || 0,
            IGSTAmount: parseFloat(detail.IGSTAmount) || 0,
            TotalAmount: parseFloat(detail.TotalAmount) || 0,
            IsActive: detail.IsActive ?? true,
            CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
            CreatedDate: modalAction === 'edit' ? detail.CreatedDate : null,
            ModifiedBy: modalAction === 'edit' ? userId : null
          };
        })
      };

      if (modalAction === 'create') {
        await postData('PurchaseOrderApi', '', dataToSend, token);
        setSuccess('Purchase order created successfully!');
        logAudit(AUDIT_ACTION.CREATE, 'PurchaseOrderApi', 0, null, { BillNumber: formData.BillNumber || '', MasterName: formData.MasterName || '' });
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Purchase Order ID is missing');
        await updateData('PurchaseOrderApi', formData.Id, dataToSend, token);
        setSuccess('Purchase order updated successfully!');
        logAudit(AUDIT_ACTION.UPDATE, 'PurchaseOrderApi', formData.Id, { ...selectedItem }, { ...formData });
      }

      handleCloseModal();
      await fetchPurchaseOrders();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} purchase order. Please try again.`);
      setLoading(false);
    }
  };

  // Verify handler
  const handleVerify = async (id) => {
    if (!id) {
      console.error('Verify: No ID provided');
      return;
    }

    console.log('Verifying PO with ID:', id, 'Token:', token ? 'Present' : 'Missing');
    setLoading(true);
    setLoadingMessage('Verifying purchase order...');
    try {
      const result = await ConvertPurchaseOrderVerify('PurchaseOrderApi', id, token);
      console.log('Verify result:', result);
      setSuccess('Purchase order verified successfully!');
      logAudit(AUDIT_ACTION.VERIFY, 'PurchaseOrderApi', id);
      handleCloseModal();
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error verifying:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setError(`Failed to verify purchase order: ${error.response?.data?.message || error.message}`);
    }
    setLoading(false);
  };

  // Convert handler
  const handleConvert = async (id) => {
    if (!id) {
      console.error('Convert: No ID provided');
      return;
    }

    console.log('Converting PO with ID:', id, 'Token:', token ? 'Present' : 'Missing');
    setLoading(true);
    setLoadingMessage('Converting to purchase...');
    try {
      const result = await ConvertPurchaseOrderToPurchase('PurchaseOrderApi', id, token);
      console.log('Convert result:', result);
      setSuccess('Purchase order converted to purchase successfully!');
      logAudit(AUDIT_ACTION.APPROVE, 'PurchaseOrderApi', id);
      handleCloseModal();
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error converting to purchase:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setError(`Failed to convert purchase order: ${error.response?.data?.message || error.message}`);
    }
    setLoading(false);
  };

  // Optimized supplier click handler
  const handleSupplierClick = useCallback(async (supplierId, supplierName) => {
    setLoading(true);
    setLoadingMessage('Fetching supplier purchase orders...');
    try {
      const response = await fetchData('PurchaseOrderApi', `/PurchaseOrderHistoryByMaster/${supplierId}/${branchId}`, token);
      const responseData = response.response_data || response || [];

      // Handle the response structure - it's an array with PurchaseOrder and PurchaseOrderDetails
      let purchaseOrdersList = [];
      let purchaseOrderDetailsList = [];

      if (Array.isArray(responseData) && responseData.length > 0) {
        // Extract PurchaseOrder and PurchaseOrderDetails from the first item
        const firstItem = responseData[0];
        purchaseOrdersList = firstItem.PurchaseOrder || firstItem.purchaseOrder || [];
        purchaseOrderDetailsList = firstItem.PurchaseOrderDetails || firstItem.purchaseOrderDetails || [];
      } else if (responseData.PurchaseOrder) {
        purchaseOrdersList = responseData.PurchaseOrder || [];
        purchaseOrderDetailsList = responseData.PurchaseOrderDetails || [];
      }

      // Map details to each purchase order
      const purchaseOrdersWithDetails = purchaseOrdersList.map(po => {
        const details = purchaseOrderDetailsList.filter(detail => detail.PurchaseOrderId === po.Id);
        return { ...po, PurchaseOrderDetails: details };
      });

      setSelectedSupplier({ id: supplierId, name: supplierName });
      setSupplierPurchaseOrders(purchaseOrdersWithDetails);
      setExpandedPOId(null);
      setSupplierModalOpen(true);
    } catch (error) {
      console.error('Error fetching supplier purchase orders:', error);
      // Fallback: filter from existing data
      const filteredPOs = purchaseOrders.filter(po => po.MasterId === supplierId || po.SupplierId === supplierId);
      setSelectedSupplier({ id: supplierId, name: supplierName });
      setSupplierPurchaseOrders(filteredPOs);
      setExpandedPOId(null);
      setSupplierModalOpen(true);
    }
    setLoading(false);
  }, [branchId, token, purchaseOrders]);

  const handleCloseSupplierModal = () => {
    setSupplierModalOpen(false);
    setSelectedSupplier(null);
    setSupplierPurchaseOrders([]);
    setExpandedPOId(null);
  };

  const togglePOExpand = async (poId) => {
    if (expandedPOId === poId) {
      setExpandedPOId(null);
    } else {
      setExpandedPOId(poId);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, poNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(poNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('PurchaseOrderApi', deleteItemId, token);
      setSuccess('Purchase order deleted successfully!');
      logAudit(AUDIT_ACTION.DELETE, 'PurchaseOrderApi', deleteItemId, { Id: deleteItemId }, null);
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete purchase order.');
    }
    setLoading(false);
  };

  // Filter and pagination - Memoized for performance
  const getFilteredData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    let filtered = data;

    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.PurchaseOrderNo?.toLowerCase().includes(searchLower) ||
        item.MasterName?.toLowerCase().includes(searchLower) ||
        item.SupplierName?.toLowerCase().includes(searchLower)
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    // Date range filter
    if (fromDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.PurchaseOrderDate?.split('T')[0];
        return itemDate >= fromDate;
      });
    }

    if (toDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.PurchaseOrderDate?.split('T')[0];
        return itemDate <= toDate;
      });
    }

    return filtered;
  }, [debouncedSearchTerm, filterStatus, filterVerified, fromDate, toDate]);

  // Memoized pagination data
  const paginationData = useMemo(() => {
    const filtered = getFilteredData(purchaseOrders) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  }, [purchaseOrders, getFilteredData, currentPage, rowsPerPage]);

  // Memoized supplier lookup
  const supplierLookup = useMemo(() => {
    const lookup = {};
    suppliers.forEach(supplier => {
      const id = supplier.MasterId || supplier.Id;
      if (id) {
        lookup[id] = supplier;
      }
    });
    return lookup;
  }, [suppliers]);

  // Memoized items lookup
  const itemsLookup = useMemo(() => {
    const lookup = {};
    items.forEach(item => {
      if (item.Id) {
        lookup[item.Id] = item;
      }
    });
    return lookup;
  }, [items]);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);
  const handleRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }, []);
  const handleRefresh = useCallback(() => fetchAllData(), []);
  const handleCloseSnackbar = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  // Print individual Purchase Order handler - Modern Format with WhatsApp Share
  const handlePrintPO = async (poId) => {
    logAudit(AUDIT_ACTION.PRINT, 'PurchaseOrderApi', poId);
    setLoading(true);
    setLoadingMessage('Preparing print...');

    try {
      const freshData = await fetchSinglePurchaseOrder(poId);

      if (!freshData) {
        setError('Failed to fetch purchase order details for printing.');
        setLoading(false);
        return;
      }

      const poData = freshData.PurchaseOrder || freshData;
      const details = freshData.PurchaseOrderDetails || freshData.Details || [];

      const supplier = suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId);
      const supplierName = poData.MasterName || supplier?.Name || '-';
      const supplierAddress = supplier?.Address || poData.SupplierAddress || '';
      const supplierGST = supplier?.GSTNumber || poData.SupplierGSTNumber || '';
      const supplierContact = supplier?.ContactNumber || supplier?.Mobile || '';
      const supplierEmail = supplier?.Email || '';
      const companyNameDisplay = companyName || 'Company Name';

      const fmt = (n) => '₹' + (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const fmtNum = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const fmtDate = (d) => { if (!d) return '-'; const dt = new Date(d); return dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }); };

      // Totals
      const totalQty = details.reduce((s, d) => s + (parseFloat(d.Quantity) || 0), 0);
      const totalTaxable = details.reduce((s, d) => s + (parseFloat(d.TaxableAmount) || 0), 0);
      const totalGST = details.reduce((s, d) => s + (parseFloat(d.CGSTAmount) || 0) + (parseFloat(d.SGSTAmount) || 0) + (parseFloat(d.IGSTAmount) || 0), 0);
      const totalDiscount = details.reduce((s, d) => s + (parseFloat(d.DiscountAmount) || 0), 0);
      const itemsTotal = details.reduce((s, d) => s + (parseFloat(d.TotalAmount) || 0), 0);
      const grandTotal = parseFloat(poData.TotalAmount) || 0;
      const isIGST = poData.InvoiceTypeName?.toLowerCase().includes('igst');

      // Item rows
      let itemRows = '';
      details.forEach((d, idx) => {
        const itemData = items.find(i => i.Id === d.ItemId);
        const itemName = d.ItemName || itemData?.Name || '-';
        const taxable = parseFloat(d.TaxableAmount) || 0;
        const cgst = parseFloat(d.CGSTAmount) || 0;
        const sgst = parseFloat(d.SGSTAmount) || 0;
        const igst = parseFloat(d.IGSTAmount) || 0;
        const gstAmt = cgst + sgst + igst;
        const bg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
        itemRows += `<tr style="background:${bg};">
          <td style="padding:7px 8px;text-align:center;border-bottom:1px solid #e2e8f0;font-size:11px;color:#64748b;">${idx + 1}</td>
          <td style="padding:7px 8px;border-bottom:1px solid #e2e8f0;font-size:11px;color:#64748b;">${d.HSNCode || '-'}</td>
          <td style="padding:7px 8px;border-bottom:1px solid #e2e8f0;font-size:11px;font-weight:500;color:#1e293b;">${itemName}</td>
          <td style="padding:7px 8px;text-align:center;border-bottom:1px solid #e2e8f0;font-size:11px;">${d.Quantity || 0}</td>
          <td style="padding:7px 8px;text-align:right;border-bottom:1px solid #e2e8f0;font-size:11px;">${fmtNum(d.Rate)}</td>
          <td style="padding:7px 8px;text-align:right;border-bottom:1px solid #e2e8f0;font-size:11px;color:#dc2626;">${d.DiscountPercentage > 0 ? d.DiscountPercentage + '%' : '-'}</td>
          <td style="padding:7px 8px;text-align:right;border-bottom:1px solid #e2e8f0;font-size:11px;">${fmtNum(taxable)}</td>
          <td style="padding:7px 8px;text-align:center;border-bottom:1px solid #e2e8f0;font-size:11px;color:#7c3aed;">${d.GSTRate || 0}%</td>
          <td style="padding:7px 8px;text-align:right;border-bottom:1px solid #e2e8f0;font-size:11px;color:#7c3aed;">${fmtNum(gstAmt)}</td>
          <td style="padding:7px 8px;text-align:right;border-bottom:1px solid #e2e8f0;font-size:11px;font-weight:600;color:#1e40af;">${fmtNum(d.TotalAmount)}</td>
        </tr>`;
      });

      // WhatsApp text summary
      const waText = encodeURIComponent(
        `*PURCHASE ORDER*\n` +
        `PO No: ${poData.PurchaseOrderNo}\n` +
        `Date: ${fmtDate(poData.PurchaseOrderDate)}\n` +
        `Supplier: ${supplierName}\n` +
        `Items: ${details.length}\n` +
        `Total Amount: ₹${fmtNum(grandTotal)}\n` +
        `From: ${companyNameDisplay}`
      );
      const waUrl = `https://wa.me/?text=${waText}`;

      const printContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>PO-${poData.PurchaseOrderNo}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;background:#f1f5f9;color:#1e293b}
  .page{background:white;max-width:900px;margin:0 auto;box-shadow:0 4px 24px rgba(0,0,0,0.10)}
  table{width:100%;border-collapse:collapse}
  @media print{
    .no-print{display:none!important}
    body{background:white}
    .page{box-shadow:none;max-width:100%}
    @page{margin:8mm;size:A4}
  }
</style>
</head><body>

<!-- Action Bar -->
<div class="no-print" style="position:fixed;top:0;left:0;right:0;background:linear-gradient(135deg,#1e40af,#3b82f6);padding:10px 20px;display:flex;justify-content:space-between;align-items:center;z-index:999;box-shadow:0 2px 8px rgba(0,0,0,0.2);">
  <div style="display:flex;align-items:center;gap:10px;">
    <span style="color:white;font-size:14px;font-weight:700;">🛒 Purchase Order</span>
    <span style="background:rgba(255,255,255,0.2);color:white;padding:2px 10px;border-radius:20px;font-size:12px;">${poData.PurchaseOrderNo}</span>
  </div>
  <div style="display:flex;gap:8px;">
    <a href="${waUrl}" target="_blank" style="padding:7px 16px;border:none;border-radius:6px;background:#25d366;color:white;cursor:pointer;font-size:12px;font-weight:600;text-decoration:none;display:flex;align-items:center;gap:5px;">
      <span>📱</span> WhatsApp
    </a>
    <button onclick="window.print()" style="padding:7px 16px;border:none;border-radius:6px;background:white;color:#1e40af;cursor:pointer;font-size:12px;font-weight:700;">🖨️ Print</button>
    <button onclick="window.close()" style="padding:7px 16px;border:1px solid rgba(255,255,255,0.4);border-radius:6px;background:transparent;color:white;cursor:pointer;font-size:12px;">✕ Close</button>
  </div>
</div>

<div class="page" style="margin-top:55px;">

  <!-- Header -->
  <div style="background:linear-gradient(135deg,#1e40af 0%,#3b82f6 100%);padding:24px 28px;display:flex;justify-content:space-between;align-items:flex-start;">
    <div style="display:flex;align-items:center;gap:14px;">
      <img src="${MampillyLogo}" alt="Logo" style="width:52px;height:52px;object-fit:contain;border-radius:6px;background:white;padding:4px;" />
      <div>
        <div style="font-size:22px;font-weight:800;color:white;letter-spacing:0.5px;">${companyNameDisplay}</div>
        <div style="color:rgba(255,255,255,0.75);font-size:11px;margin-top:4px;">Purchase Department</div>
      </div>
    </div>
    <div style="text-align:right;">
      <div style="background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);border-radius:8px;padding:10px 16px;">
        <div style="color:rgba(255,255,255,0.7);font-size:10px;text-transform:uppercase;letter-spacing:1px;">Purchase Order</div>
        <div style="color:white;font-size:18px;font-weight:800;margin-top:2px;">${poData.PurchaseOrderNo}</div>
        <div style="color:rgba(255,255,255,0.8);font-size:11px;margin-top:4px;">${fmtDate(poData.PurchaseOrderDate)}</div>
      </div>
    </div>
  </div>

  <!-- Info Cards -->
  <div style="display:flex;gap:0;border-bottom:2px solid #e2e8f0;">
    <div style="flex:1;padding:18px 24px;border-right:1px solid #e2e8f0;">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;margin-bottom:8px;">Supplier</div>
      <div style="font-size:14px;font-weight:700;color:#1e293b;">${supplierName}</div>
      ${supplierAddress ? `<div style="font-size:11px;color:#64748b;margin-top:3px;">${supplierAddress}</div>` : ''}
      ${supplierGST ? `<div style="font-size:11px;color:#64748b;margin-top:3px;">GST: <strong>${supplierGST}</strong></div>` : ''}
      ${supplierContact ? `<div style="font-size:11px;color:#64748b;margin-top:2px;">📞 ${supplierContact}</div>` : ''}
      ${supplierEmail ? `<div style="font-size:11px;color:#64748b;margin-top:2px;">✉ ${supplierEmail}</div>` : ''}
    </div>
    <div style="flex:1;padding:18px 24px;border-right:1px solid #e2e8f0;">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;margin-bottom:8px;">Order Details</div>
      <table style="font-size:11px;">
        <tr><td style="color:#64748b;padding:2px 0;width:90px;">PO Number</td><td style="font-weight:600;color:#1e293b;">: ${poData.PurchaseOrderNo}</td></tr>
        <tr><td style="color:#64748b;padding:2px 0;">Date</td><td style="font-weight:600;color:#1e293b;">: ${fmtDate(poData.PurchaseOrderDate)}</td></tr>
        <tr><td style="color:#64748b;padding:2px 0;">Fin. Year</td><td style="font-weight:600;color:#1e293b;">: ${poData.FinYear || '-'}</td></tr>
        <tr><td style="color:#64748b;padding:2px 0;">GST Type</td><td style="font-weight:600;color:#1e293b;">: ${poData.GstCalculationTypeName || 'Excluding Tax'}</td></tr>
      </table>
    </div>
    <div style="flex:1;padding:18px 24px;">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;margin-bottom:8px;">Deliver To</div>
      <div style="font-size:13px;font-weight:700;color:#1e293b;">${companyNameDisplay}</div>
      <div style="font-size:11px;color:#64748b;margin-top:3px;">As per company address</div>
    </div>
  </div>

  <!-- Items Table -->
  <div style="padding:0;">
    <table>
      <thead>
        <tr style="background:#f8fafc;border-bottom:2px solid #e2e8f0;">
          <th style="padding:10px 8px;text-align:center;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:4%;">#</th>
          <th style="padding:10px 8px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:7%;">HSN</th>
          <th style="padding:10px 8px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:29%;">Item / Description</th>
          <th style="padding:10px 8px;text-align:center;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:5%;">Qty</th>
          <th style="padding:10px 8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:8%;">Rate</th>
          <th style="padding:10px 8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:6%;">Disc%</th>
          <th style="padding:10px 8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:10%;">Taxable</th>
          <th style="padding:10px 8px;text-align:center;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:6%;">GST%</th>
          <th style="padding:10px 8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:9%;">GST Amt</th>
          <th style="padding:10px 8px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:700;width:10%;">Total</th>
        </tr>
      </thead>
      <tbody>${itemRows}</tbody>
      <tfoot>
        <tr style="background:#f8fafc;border-top:2px solid #e2e8f0;">
          <td colspan="3" style="padding:8px;font-size:11px;font-weight:600;color:#64748b;">Total (${details.length} items)</td>
          <td style="padding:8px;text-align:center;font-size:11px;font-weight:700;">${fmtNum(totalQty)}</td>
          <td></td>
          <td style="padding:8px;text-align:right;font-size:11px;color:#dc2626;font-weight:600;">${totalDiscount > 0 ? '-' + fmtNum(totalDiscount) : '-'}</td>
          <td style="padding:8px;text-align:right;font-size:11px;font-weight:700;">${fmtNum(totalTaxable)}</td>
          <td></td>
          <td style="padding:8px;text-align:right;font-size:11px;font-weight:700;color:#7c3aed;">${fmtNum(totalGST)}</td>
          <td style="padding:8px;text-align:right;font-size:12px;font-weight:800;color:#1e40af;">${fmtNum(itemsTotal)}</td>
        </tr>
      </tfoot>
    </table>
  </div>

  <!-- Summary + Notes -->
  <div style="display:flex;gap:0;border-top:2px solid #e2e8f0;">
    <div style="flex:1;padding:18px 24px;border-right:1px solid #e2e8f0;">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;margin-bottom:10px;">Terms & Notes</div>
      <div style="font-size:11px;color:#475569;line-height:1.8;">
        <div>• Material must conform to approved specifications.</div>
        <div>• PO No. must appear on all invoices.</div>
        <div>• GST Number must be mentioned on invoice.</div>
        ${poData.Instruction1 ? `<div>• ${poData.Instruction1}</div>` : ''}
        ${poData.Instruction2 ? `<div>• ${poData.Instruction2}</div>` : ''}
        ${poData.Remarks ? `<div style="margin-top:6px;color:#1e293b;font-weight:500;">Remarks: ${poData.Remarks}</div>` : ''}
      </div>
    </div>
    <div style="width:260px;padding:18px 24px;">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;margin-bottom:10px;">Summary</div>
      <table style="font-size:11px;width:100%;">
        <tr><td style="color:#64748b;padding:3px 0;">Subtotal</td><td style="text-align:right;font-weight:500;">${fmt(details.reduce((s, d) => s + (parseFloat(d.Amount) || 0), 0))}</td></tr>
        ${totalDiscount > 0 ? `<tr><td style="color:#dc2626;padding:3px 0;">Discount</td><td style="text-align:right;color:#dc2626;font-weight:500;">-${fmt(totalDiscount)}</td></tr>` : ''}
        <tr><td style="color:#64748b;padding:3px 0;">Taxable Amount</td><td style="text-align:right;font-weight:500;">${fmt(totalTaxable)}</td></tr>
        ${!isIGST ? `
        <tr><td style="color:#7c3aed;padding:3px 0;">CGST</td><td style="text-align:right;color:#7c3aed;font-weight:500;">${fmt(details.reduce((s, d) => s + (parseFloat(d.CGSTAmount) || 0), 0))}</td></tr>
        <tr><td style="color:#7c3aed;padding:3px 0;">SGST</td><td style="text-align:right;color:#7c3aed;font-weight:500;">${fmt(details.reduce((s, d) => s + (parseFloat(d.SGSTAmount) || 0), 0))}</td></tr>
        ` : `<tr><td style="color:#7c3aed;padding:3px 0;">IGST</td><td style="text-align:right;color:#7c3aed;font-weight:500;">${fmt(details.reduce((s, d) => s + (parseFloat(d.IGSTAmount) || 0), 0))}</td></tr>`}
        ${poData.FreightAmount > 0 ? `<tr><td style="color:#64748b;padding:3px 0;">Freight</td><td style="text-align:right;font-weight:500;">${fmt(poData.FreightAmount)}</td></tr>` : ''}
        ${poData.FreightAmount > 0 && poData.FreightGSTAmount > 0 ? `<tr><td style="color:#7c3aed;padding:3px 0;">Freight GST (${Math.round(poData.FreightGSTRate || 0)}%)</td><td style="text-align:right;color:#7c3aed;font-weight:500;">${fmt(poData.FreightGSTAmount)}</td></tr>` : ''}
        ${poData.PackingAmount > 0 ? `<tr><td style="color:#64748b;padding:3px 0;">Packing</td><td style="text-align:right;font-weight:500;">${fmt(poData.PackingAmount)}</td></tr>` : ''}
        ${poData.PackingAmount > 0 && poData.PackingGSTAmount > 0 ? `<tr><td style="color:#7c3aed;padding:3px 0;">Packing GST (${Math.round(poData.PackingGSTRate || 0)}%)</td><td style="text-align:right;color:#7c3aed;font-weight:500;">${fmt(poData.PackingGSTAmount)}</td></tr>` : ''}
        ${poData.RoundOff ? `<tr><td style="color:#64748b;padding:3px 0;">Round Off</td><td style="text-align:right;font-weight:500;">${(parseFloat(poData.RoundOff) || 0) >= 0 ? '+' : ''} ${(parseFloat(poData.RoundOff) || 0).toFixed(2)}</td></tr>` : ''}
        <tr><td colspan="2"><div style="border-top:2px solid #1e40af;margin:6px 0;"></div></td></tr>
        <tr><td style="font-size:13px;font-weight:800;color:#1e40af;padding:3px 0;">Grand Total</td><td style="text-align:right;font-size:14px;font-weight:800;color:#1e40af;">${fmt(grandTotal)}</td></tr>
      </table>
    </div>
  </div>

  <!-- Signature -->
  <div style="display:flex;border-top:2px solid #e2e8f0;">
    <div style="flex:1;padding:30px 24px 16px;border-right:1px solid #e2e8f0;text-align:center;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">Prepared By</div>
    </div>
    <div style="flex:1;padding:30px 24px 16px;border-right:1px solid #e2e8f0;text-align:center;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">Checked By</div>
    </div>
    <div style="flex:1;padding:30px 24px 16px;text-align:center;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">Authorized Signatory</div>
    </div>
  </div>

  <!-- Footer -->
  <div style="background:#f8fafc;padding:10px 24px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center;">
    <div style="font-size:10px;color:#94a3b8;">${companyNameDisplay} — This is a computer generated document.</div>
    <div style="font-size:10px;color:#94a3b8;">PO: ${poData.PurchaseOrderNo} | ${fmtDate(poData.PurchaseOrderDate)}</div>
  </div>

</div>
</body></html>`;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
      } else {
        setError('Popup blocked. Please allow popups for this site.');
      }

    } catch (error) {
      console.error('Error printing PO:', error);
      setError('Failed to print purchase order.');
    }

    setLoading(false);
  };

  // Memoized table row component for better performance
  const PurchaseOrderRow = useMemo(() => {
    return ({ item, index, startIndex, supplierLookup, handleOpenModal, handleSupplierClick, handleOpenDeleteModal, handlePrintPO, handleOpenInactiveModal, handleOpenActivateModal }) => {
      const supplier = supplierLookup[item.MasterId || item.SupplierId];
      const supplierName = item.MasterName || supplier?.Name || '-';

      return (
        <tr key={item.Id} style={{ backgroundColor: !item.IsActive ? '#fef2f2' : 'inherit' }}>
          <td>
            {item.IsActive ? (
              <span onClick={() => handleOpenModal('edit', item)} style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                {startIndex + index + 1}
              </span>
            ) : (
              <span style={{ fontWeight: 500, color: '#6b7280' }}>{startIndex + index + 1}</span>
            )}
          </td>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShoppingCart sx={{ fontSize: 18, color: item.IsActive ? '#1e40af' : '#6b7280' }} />
              {item.PurchaseOrderNo || '-'}
            </div>
          </td>
          <td>{formatDate(item.PurchaseOrderDate)}</td>
          <td>
            {item.IsActive ? (
              <span
                onClick={() => handleSupplierClick(item.MasterId || item.SupplierId, supplierName)}
                style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
              >
                {supplierName}
              </span>
            ) : (
              <span style={{ color: '#6b7280' }}>{supplierName}</span>
            )}
          </td>
          <td style={{ fontWeight: 600 }}>{formatCurrency(item.TotalAmount)}</td>
          <td>
            {item.IsVerified ? (
              <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
            ) : (
              <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
            )}
          </td>
          <td>
            {item.IsConverted ? (
              <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
            ) : (
              <Chip label="Open" color="default" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
            )}
          </td>
          <td>
            {item.IsActive ? (
              <Chip label="Active" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
            ) : (
              <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />
            )}
          </td>
          <td style={{ textAlign: 'right' }}>
            <div className="action-buttons">
              {item.IsActive ? (
                /* Active item actions: View, Edit, Delete, Print */
                <>
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => handleOpenModal('view', item.Id)}>
                      <Visibility style={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                  {hasEditMasterPermission && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenModal('edit', item.Id)} disabled={item.IsVerified || item.IsConverted}>
                        <Edit style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasDeleteMasterPermission && (
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleOpenDeleteModal(item.Id, item.PurchaseOrderNo)} disabled={item.IsVerified || item.IsConverted}>
                        <Delete style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {item.IsVerified && (
                    <Tooltip title="Print">
                      <IconButton size="small" onClick={() => handlePrintPO(item.Id)} className="print-action-btn">
                        <Print style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              ) : (
                /* Inactive item actions: View, Activate */
                <>
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => handleOpenInactiveModal(item)}>
                      <Visibility style={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                  {hasEditMasterPermission && (
                    <Tooltip title="Activate">
                      <IconButton size="small" onClick={() => handleOpenActivateModal(item.Id, item.PurchaseOrderNo)} sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
                        <CheckCircleOutline style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      );
    };
  }, [hasEditMasterPermission, hasDeleteMasterPermission]);

  // Format helpers - memoized
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, []);

  const formatCurrency = useCallback((amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  const { data: paginatedData, totalCount, totalPages, startIndex } = paginationData;


  // Stats
  const totalOrders = purchaseOrders.length;
  const activeOrders = purchaseOrders.filter(p => p.IsActive).length;
  const verifiedOrders = purchaseOrders.filter(p => p.IsVerified).length;
  const totalAmount = purchaseOrders.filter(p => p.IsActive).reduce((s, p) => s + (p.TotalAmount || 0), 0);

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Romans', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Purchase Order</h4>
        </div>

        {/* Stats Cards */}
        {/* <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Orders', value: totalOrders, color: '#2C3E6E' },
            { label: 'Active', value: activeOrders, color: '#16a34a' },
            { label: 'Verified', value: verifiedOrders, color: '#0891b2' },
            { label: 'Total Amount', value: `₹${totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: '#7c3aed' },
          ].map((card, i) => (
            <div key={i} style={{ flex: '1 1 120px', background: '#f8f9fa', borderRadius: '8px', padding: '8px 12px', borderLeft: `3px solid ${card.color}` }}>
              <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{card.label}</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: card.color, marginTop: '2px' }}>{card.value}</div>
            </div>
          ))}
        </div> */}

        {/* Search, Filters and Create */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, overflowY: 'visible' }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
              </select>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              {hasCreateMasterPermission && (
                <Tooltip title="Add Purchase Order" arrow>
                  <IconButton onClick={() => handleOpenModal('create')} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                    <Add sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by PO No, Supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by PO No, Supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            {hasCreateMasterPermission && (
              <Tooltip title="Add Purchase Order" arrow>
                <Button variant="contained" onClick={() => handleOpenModal('create')} sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                  <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Add PO</span>
                </Button>
              </Tooltip>
            )}
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'PO No.', 'Date', 'Supplier', 'Total Amount', 'Verified', 'Converted', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 8 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => {
                  const supplierName = item.MasterName || supplierLookup[item.MasterId || item.SupplierId]?.Name || '-';
                  return (
                    <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>{item.PurchaseOrderNo || '-'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.PurchaseOrderDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.IsActive ? (
                          <span onClick={() => handleSupplierClick(item.MasterId || item.SupplierId, supplierName)} style={{ color: '#2C3E6E', cursor: 'pointer', textDecoration: 'underline' }}>{supplierName}</span>
                        ) : supplierName}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>{formatCurrency(item.TotalAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsVerified ? 'Verified' : 'Pending'} color={item.IsVerified ? 'success' : 'warning'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsConverted ? 'Converted' : 'Open'} color={item.IsConverted ? 'info' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {item.IsActive ? (
                            <>
                              <Tooltip title="View" arrow>
                                <Button size="small" onClick={() => handleOpenModal('view', item.Id)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button>
                              </Tooltip>
                              {hasEditMasterPermission && (
                                <Tooltip title="Edit" arrow>
                                  <Button size="small" onClick={() => handleOpenModal('edit', item.Id)} disabled={item.IsVerified || item.IsConverted} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button>
                                </Tooltip>
                              )}
                              {hasDeleteMasterPermission && (
                                <Tooltip title="Delete" arrow>
                                  <Button size="small" onClick={() => handleOpenDeleteModal(item.Id, item.PurchaseOrderNo)} disabled={item.IsVerified || item.IsConverted} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button>
                                </Tooltip>
                              )}
                              {item.IsVerified && (
                                <Tooltip title="Print" arrow>
                                  <Button size="small" onClick={() => handlePrintPO(item.Id)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🖨️</Button>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              <Tooltip title="View" arrow>
                                <Button size="small" onClick={() => handleOpenInactiveModal(item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button>
                              </Tooltip>
                              {hasEditMasterPermission && (
                                <Tooltip title="Activate" arrow>
                                  <Button size="small" onClick={() => handleOpenActivateModal(item.Id, item.PurchaseOrderNo)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✅</Button>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '13px' }}>No purchase orders found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={rowsPerPage}
          onItemsPerPageChange={(v) => { setRowsPerPage(Number(v)); setCurrentPage(1); }}
          totalItems={totalCount}
        />
      </div>

      <LogoLoader open={loading} message={loadingMessage} variant="default" />

      <PurchaseOrderModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
        onSave={handleSave}
        onVerify={handleVerify}
        onConvert={handleConvert}
        suppliers={suppliers}
        items={items}
        loading={loading}
        freightPackingGST={freightPackingGST}
        onFetchPOHistory={fetchPOHistoryForModal}
        onFetchItems={fetchItems}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success">{success}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error">{error}</Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Purchase Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete purchase order <strong style={{ color: '#1e293b' }}>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog open={activateModalOpen} onClose={handleCloseActivateModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#f0fdf4', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircleOutline sx={{ color: '#10b981', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Activate Purchase Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to activate purchase order <strong style={{ color: '#1e293b' }}>{activateItemNo}</strong>? This will restore the purchase order to active status.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseActivateModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
            Cancel
          </Button>
          <Button onClick={handleActivate} variant="contained" sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}>
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Purchase Orders Modal */}
      <Dialog
        open={supplierModalOpen}
        onClose={handleCloseSupplierModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#1e40af', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Purchase Orders - {selectedSupplier?.name}
              </Typography>
              <Chip
                label={`${supplierPurchaseOrders.length} Orders`}
                color="primary"
                size="small"
                sx={{ fontSize: '0.7rem', height: '22px' }}
              />
            </Box>
            <IconButton onClick={handleCloseSupplierModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
            {supplierPurchaseOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
                <Typography>No purchase orders found for this supplier</Typography>
              </Box>
            ) : (
              supplierPurchaseOrders.map((po, poIndex) => (
                <Box key={po.Id} sx={{ borderBottom: '1px solid #e2e8f0' }}>
                  {/* PO Header Row - Clickable */}
                  <Box
                    onClick={() => togglePOExpand(po.Id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      cursor: 'pointer',
                      bgcolor: expandedPOId === po.Id ? '#f0f9ff' : (poIndex % 2 === 0 ? '#fff' : '#f9fafb'),
                      '&:hover': { bgcolor: '#f0f9ff' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {expandedPOId === po.Id ? <ExpandLess sx={{ color: '#1e40af' }} /> : <ExpandMore sx={{ color: '#64748b' }} />}
                        <Typography sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.9rem' }}>
                          {po.PurchaseOrderNo}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                        {formatDate(po.PurchaseOrderDate)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {po.IsVerified ? (
                          <Chip icon={<CheckCircle sx={{ fontSize: 12 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        ) : (
                          <Chip label="Pending" color="warning" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        )}
                        {po.IsConverted && (
                          <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#1e40af', fontSize: '0.95rem' }}>
                      {formatCurrency(po.TotalAmount)}
                    </Typography>
                  </Box>

                  {/* PO Details - Expandable */}
                  <Collapse in={expandedPOId === po.Id}>
                    <Box sx={{ bgcolor: '#f8fafc', p: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', mb: 1, display: 'block' }}>
                        Order Details ({po.PurchaseOrderDetails?.length || 0} items)
                      </Typography>
                      <Box sx={{ overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                          <thead>
                            <tr style={{ background: '#e2e8f0' }}>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>#</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Item</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>HSN</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Qty</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Rate</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Amount</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>GST%</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(po.PurchaseOrderDetails || []).map((detail, detailIndex) => (
                              <tr key={detail.Id || detailIndex} style={{ borderBottom: '1px solid #e2e8f0', background: detailIndex % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                <td style={{ padding: '8px', textAlign: 'left' }}>{detailIndex + 1}</td>
                                <td style={{ padding: '8px', textAlign: 'left', fontWeight: 500 }}>
                                  {detail.ItemName || '-'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{detail.HSNCode || '-'}</td>
                                <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{detail.Quantity || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Amount)}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{detail.GSTRate || 0}%</td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount)}</td>
                              </tr>
                            ))}
                            {(!po.PurchaseOrderDetails || po.PurchaseOrderDetails.length === 0) && (
                              <tr>
                                <td colSpan="8" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No details available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Box>
                      {/* Summary Row */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 1.5, pt: 1, borderTop: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Taxable: <strong style={{ color: '#374151' }}>{formatCurrency(po.TaxableAmount)}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          GST: <strong style={{ color: '#166534' }}>{formatCurrency((po.CGSTAmount || 0) + (po.SGSTAmount || 0) + (po.IGSTAmount || 0))}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Total: <strong style={{ color: '#1e40af' }}>{formatCurrency(po.TotalAmount)}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Total Orders: <strong>{supplierPurchaseOrders.length}</strong> |
            Total Amount: <strong style={{ color: '#1e40af' }}>{formatCurrency(supplierPurchaseOrders.reduce((sum, po) => sum + (po.TotalAmount || 0), 0))}</strong>
          </Typography>
          <Button onClick={handleCloseSupplierModal} variant="outlined" size="small" sx={{ borderColor: '#d1d5db', color: '#374151' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inactive Purchase Order View Modal */}
      <Dialog
        open={inactiveModalOpen}
        onClose={handleCloseInactiveModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fef2f2' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#dc2626', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                View Inactive Purchase Order
              </Typography>
              <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />
            </Box>
            <IconButton onClick={handleCloseInactiveModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {inactiveViewData && (
            <>
              {/* Order Information */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Order Information</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>PO Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{inactiveViewData.PurchaseOrderNo || '-'}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(inactiveViewData.PurchaseOrderDate)}</Typography>
                </Box>
                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{inactiveViewData.MasterName || '-'}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{formatCurrency(inactiveViewData.TotalAmount)}</Typography>
                </Box>
              </Box>

              {/* Order Items */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Order Items ({inactiveViewDetails.length} items)</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#fee2e2' }}>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#991b1b' }}>Item</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>HSN</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>GST%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inactiveViewDetails.map((detail, index) => (
                        <tr key={detail.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '8px', fontWeight: 500 }}>{detail.ItemName || items.find(i => i.Id === detail.ItemId)?.Name || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.HSNCode || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{detail.Quantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.GSTRate || 0}%</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount)}</td>
                        </tr>
                      ))}
                      {inactiveViewDetails.length === 0 && (
                        <tr>
                          <td colSpan="7" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Order Summary */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Order Summary</Typography>
              <Box sx={{ bgcolor: '#fef2f2', borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Sub Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(inactiveViewData.Amount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Discount:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#dc2626' }}>- {formatCurrency(inactiveViewData.DiscountAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Taxable:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(inactiveViewData.TaxableAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">GST:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#166534' }}>{formatCurrency((inactiveViewData.CGSTAmount || 0) + (inactiveViewData.SGSTAmount || 0) + (inactiveViewData.IGSTAmount || 0))}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626' }}>Grand Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.9rem' }}>{formatCurrency(inactiveViewData.TotalAmount)}</Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#fef2f2' }}>
          <Button onClick={handleCloseInactiveModal} variant="outlined" size="small" sx={{ borderColor: '#dc2626', color: '#dc2626', '&:hover': { borderColor: '#b91c1c', bgcolor: '#fee2e2' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PurchaseOrderIndex;

